import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Grid, Typography, CircularProgress, Alert, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { useNavItems, usePermission, useFileUpload } from '../../../hooks';
import {
  UploadContainer,
  UploadTitle,
  UploadButton,
  ChooseFileButton,
  MainContent
} from './e09Upload.styled';
import { ErrorTable } from '../../../common/components/ErrorTable';

// Define metadata interface
interface FileMetadata {
  fileId: string | null;
  fileName: string;
  operatorByFileUploader: string;
  operatorByInboundPpo: string;
  operatorByOutboundPpo: string;
  accountingYear: number;
  accountingQuarter: number;
  fileVersion: number;
  creationDate: string;
  inboundRemovalDate: string | null;
  outboundRemovalDate: string | null;
  fileStatus: string;
  processDate: string | null;
  inboundAcceptanceDate: string | null;
  outboundAcceptanceDate: string | null;
  notifiedDate: string | null;
  downloadDate: string | null;
  validStatus: string | null;
  samplesCount: number | null;
  pendingOn: string;
  loggedUserOperator: string;
  deletedAt: string | null;
  comment: string | null;
  samples: any | null;
}

interface ValidationError {
  sampleId: number | null;
  field: string;
  errorCode: string;
  message: string;
  version: string | number | null;
}

interface FileUploadError {
  message: string;
  details?: string[];
  code?: string;
  validationErrors?: ValidationError[];
}

const E09Upload: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [successNotification, setSuccessNotification] = useState<boolean>(false);
  const [fileUploaded, setFileUploaded] = useState<boolean>(false);
  const [fileMetadata, setFileMetadata] = useState<FileMetadata | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const navigate = useNavigate();
  const { t } = useTranslation();
  const navItems = useNavItems();
  const { hasPermission, isLoading } = usePermission('/e09', '/upload');
  
  // Use the file upload hook
  const { 
    selectedFile,
    isUploading,
    uploadError,
    validationErrors,
    handleFileChange,
    handleUpload,
    uploadToEndpoint,
    resetUpload
  } = useFileUpload({
    onUploadSuccess: (response: any) => {
      console.log('Upload success response:', response);
      
      // Store metadata from response
      if (response && response.metadata) {
        setFileMetadata(response.metadata);
      }
      
      // Mark that initial file upload was successful
      setFileUploaded(true);
    },
    allowedFileTypes: ['.csv'],
    maxFileSizeMB: 5,
    uploadEndpoint: '/data-set/e09'
  });
  
  // Handle final upload
  const handleFinalUpload = useCallback(async () => {
    try {
      const result = await uploadToEndpoint('/data-set/e09/record?isFinal=true');
      if (result) {
        setUploadMessage('File has been successfully uploaded as Final!');
        setSuccessNotification(true);
        navigate(`/e09/despatches`);
        setFileUploaded(false);
        setFileMetadata(null);
        // Completely reset the file selection
        resetUpload();
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      // Error handling is done by the useFileUpload hook
      console.error('Error uploading as Final:', error);
    }
  }, [uploadToEndpoint, resetUpload]);
  
  // Handle draft upload
  const handleDraftUpload = useCallback(async () => {
    try {
      const result = await uploadToEndpoint('/data-set/e09/record?isFinal=false');
      if (result) {
        setUploadMessage('File has been successfully uploaded as Draft!');
        setSuccessNotification(true);
        
        // Extract fileId from the response for redirection
        if (result.metadata && result.metadata.fileId) {
          // Navigate to the despatches page with the fileId
          navigate(`/e09/despatches/${result.metadata.fileId}?editMode=true`);
        } else {
          // Reset states if no fileId is available
          setFileUploaded(false);
          setFileMetadata(null);
          // Completely reset the file selection
          resetUpload();
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      }
    } catch (error) {
      // Error handling is done by the useFileUpload hook
      console.error('Error uploading as Draft:', error);
    }
  }, [uploadToEndpoint, resetUpload, navigate]);
  
  // Handle Cancel action
  const handleCancelUpload = useCallback(() => {
    resetUpload();
    setFileUploaded(false);
    setFileMetadata(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [resetUpload]);
  
  // Get all errors to display in the table
  const allErrors = React.useMemo(() => {
    const errors = [...validationErrors];
    
    if (uploadError && !validationErrors.length) {
      errors.push({
        sampleId: null,
        field: 'General',
        errorCode: uploadError.code || 'ERROR',
        message: uploadError.message,
        version: null
      });
      
      if (uploadError.details && uploadError.details.length > 0) {
        uploadError.details.forEach(detail => {
          errors.push({
            sampleId: null,
            field: 'Detail',
            errorCode: 'INFO',
            message: detail,
            version: null
          });
        });
      }
      
      if (uploadError.validationErrors && uploadError.validationErrors.length > 0) {
        errors.push(...uploadError.validationErrors);
      }
    }
    
    return errors;
  }, [uploadError, validationErrors]);
  
  const handleNotificationClose = useCallback(() => {
    setSuccessNotification(false);
    resetUpload();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [resetUpload]);

  const handleSidebarToggle = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/e09');
  };

  const handleChooseFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };
  
  // Initial file upload handler
  const handleInitialUpload = async () => {
    try {
      await handleUpload();
      // The fileUploaded state will be set by the onUploadSuccess callback
    } catch (error) {
      // Error handling is done by the useFileUpload hook
      console.error('Error in initial upload:', error);
    }
  };

  useEffect(() => {
    if (!selectedFile && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedFile]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasPermission) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6" color="error">
          You do not have permission to upload e09 files
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid="e09-upload-container">
      <Grid container>
        <Grid item xs={12}>
          <SectionBar
            title={"e09 Upload"}
            subtitle={"e09 IBRS"}
            onToggleSidebar={handleSidebarToggle}
            onSubtitleClick={handleSubtitleClick}
            isSidebarOpen={sidebarOpen}
            showFilters={{}}
            data-testid="e09-upload-section-bar"
          />
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          <Sidebar 
            isOpen={sidebarOpen} 
            onToggle={handleSidebarToggle}
            navItems={navItems}
            data-testid="e09-upload-sidebar"
          />

          <MainContent data-testid="e09-upload-main-content">
            <UploadContainer data-testid="e09-upload-content">
              <UploadTitle data-testid="e09-upload-title">e09 Upload</UploadTitle>
              
              {/* Initial file selection UI */}
              {!fileUploaded && (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }} data-testid="e09-upload-initial-ui">
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2
                  }} data-testid="e09-upload-file-controls">
                    <input
                      type="file"
                      id="e09-file-upload"
                      ref={fileInputRef}
                      hidden
                      onChange={handleFileChange}
                      accept=".csv"
                      disabled={isUploading}
                      data-testid="e09-upload-file-input"
                    />
                    <ChooseFileButton
                      variant="outlined"
                      disabled={isUploading}
                      onClick={handleChooseFileClick}
                      data-testid="e09-upload-choose-file-btn"
                    >
                      { 'Choose File' }
                    </ChooseFileButton>
                    
                    <UploadButton
                      variant="contained"
                      onClick={handleInitialUpload}
                      disabled={!selectedFile || isUploading}
                      data-testid="e09-upload-initial-btn"
                    >
                      {isUploading ? 'Uploading' : 'Upload'}
                    </UploadButton>
                  </Box>
                  
                  {!selectedFile && !isUploading && (
                    <Typography sx={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }} data-testid="e09-upload-no-file-msg">
                      { 'Please select a CSV file first. The Upload button will be enabled after file selection.' }
                    </Typography>
                  )}  
                  
                  {selectedFile && !isUploading && allErrors.length === 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid="e09-upload-selected-file">
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, mr: 1 }}>
                        { 'Selected File' }:
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#00B6BA' }}>
                        {selectedFile.name}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              
              {/* Final/Draft upload UI - shown after initial upload */}
              {fileUploaded && selectedFile && !isUploading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }} data-testid="e09-upload-final-ui">
                  <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid="e09-upload-ready-file">
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, mr: 1 }}>
                      Ready to upload file:
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#00B6BA', fontWeight: 'bold' }}>
                      {selectedFile.name}
                    </Typography>
                  </Box>
                  
                  {fileMetadata && (
                    <Box sx={{ 
                      backgroundColor: '#f5f9fa', 
                      p: 2, 
                      borderRadius: 1, 
                      border: '1px solid #e0f2f1',
                      mb: 2
                    }} data-testid="e09-upload-metadata">
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>
                        File Validated Successfully
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} data-testid="e09-upload-metadata-left">
                          <Typography sx={{ fontSize: '13px', color: '#555' }}>
                            <strong>File Name:</strong> {fileMetadata.fileName}
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#555' }}>
                            <strong>Status:</strong> {fileMetadata.fileStatus}
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#555' }}>
                            <strong>Created:</strong> {new Date(fileMetadata.creationDate).toLocaleString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} data-testid="e09-upload-metadata-right">
                          <Typography sx={{ fontSize: '13px', color: '#555' }}>
                            <strong>Year/Quarter:</strong> {fileMetadata.accountingYear} / Q{fileMetadata.accountingQuarter}
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#555' }}>
                            <strong>Version:</strong> {fileMetadata.fileVersion}
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#555' }}>
                            <strong>Pending on:</strong> {fileMetadata.pendingOn}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  
                  <Typography sx={{ fontSize: '14px' }} data-testid="e09-upload-choice-prompt">
                    Please choose how to upload this file:
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    mt: 1,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' }
                  }} data-testid="e09-upload-actions">
                    <UploadButton
                      variant="contained"
                      onClick={handleFinalUpload}
                      color="primary"
                      disabled={isUploading}
                      sx={{ 
                        flex: { xs: '1 1 auto', sm: '0 1 auto' },
                        backgroundColor: '#00B6BA',
                        '&:hover': {
                          backgroundColor: '#009B9F',
                        },
                      }}
                      data-testid="e09-upload-final-btn"
                    >
                      {isUploading ? 'UPLOADING...' : 'Upload as Final'}
                    </UploadButton>
                    
                    <UploadButton
                      variant="contained"
                      onClick={handleDraftUpload}
                      color="secondary"
                      disabled={isUploading || fileMetadata?.fileVersion === 0}
                      sx={{ 
                        flex: { xs: '1 1 auto', sm: '0 1 auto' },
                        backgroundColor: '#6c757d',
                        '&:hover': {
                          backgroundColor: '#5a6268',
                        },
                      }}
                      data-testid="e09-upload-draft-btn"
                    >
                      {isUploading ? 'UPLOADING...' : 'Upload as Draft'}
                    </UploadButton>
                    
                    <ChooseFileButton
                      variant="outlined"
                      onClick={handleCancelUpload}
                      disabled={isUploading}
                      sx={{ flex: { xs: '1 1 auto', sm: '0 1 auto' } }}
                      data-testid="e09-upload-cancel-btn"
                    >
                      Cancel
                    </ChooseFileButton>
                  </Box>
                </Box>
              )}
              
              {isUploading && (
                <Box sx={{ 
                  width: '100%', 
                  mt: 2, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 1 
                }} data-testid="e09-upload-loading">
                  <CircularProgress 
                    size={40}
                    sx={{ color: '#00B6BA' }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    { 'Uploading File... Please wait' }
                  </Typography>
                </Box>
              )}

              {allErrors.length > 0 && (
                <ErrorTable 
                  errors={allErrors} 
                  fileName={selectedFile?.name}
                />
              )}
            </UploadContainer>
          </MainContent>
        </Grid>
      </Grid>
      
      <NotificationSnackbar
        open={successNotification}
        message={uploadMessage || 'Upload Success'}
        severity="success"
        onClose={handleNotificationClose}
        data-testid="e09-upload-notification"
      />
    </Box>
  );
};

export default E09Upload; 